import React, { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

import ModalBase from 'LuxKit/Modal/ModalBase'

import EnterCode from './EnterCode'
import Success from './Success'
import { RedeemedCard } from './common'
import config from 'constants/config'
import noop from 'lib/function/noop'
import { redeemGiftCard } from 'actions/GiftCardActions'
import { validateGiftCard } from './utils'
import getPayableAfterModifiers from 'checkout/selectors/payment/getPayableAfterModifiers'

interface Props {
  checkoutCurrency?: string
  onRedeem?: () => void
}

function RedeemGiftCard({ checkoutCurrency, onRedeem: onRedeemProp = noop }: Props) {
  const redeemedCards = useAppSelector((state: App.State) => state.redeemedGiftCards.cards)
  const [currentStep, setCurrentStep] = useState<'enterCode' | 'success'>(redeemedCards.length > 0 ? 'success' : 'enterCode')
  const [redeemedCardsV1, setRedeemedCardsV1] = useState<Array<RedeemedCard>>([])
  const [hasCamera, setHasCamera] = useState(false)
  const dispatch = useAppDispatch()
  const payable = useAppSelector(getPayableAfterModifiers)
  const isGiftCardV2 = config.ENABLE_GIFT_CARD_V2
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices()
      .then(devices => {
        if (devices.some(device => device.kind === 'videoinput')) {
          setHasCamera(true)
        }
      })
      .catch(() => {
        setHasCamera(false)
      })
  }, [])

  const onRedeem = useCallback((redeemedCard: RedeemedCard) => {
    if (isGiftCardV2 && !isSpoofed) {
      validateGiftCard(redeemedCards, redeemedCard, payable)
      dispatch(redeemGiftCard(redeemedCard))
    } else {
      setRedeemedCardsV1((prevRedeemedCards) => [...prevRedeemedCards, redeemedCard])
    }

    setCurrentStep('success')
    onRedeemProp()
  }, [dispatch, onRedeemProp, redeemedCards, payable, isGiftCardV2, isSpoofed])

  const moveToSummary = useCallback(() => {
    setCurrentStep('success')
  }, [])

  return (
    <ModalBase height="full" tabletHeight="auto">
      {currentStep === 'enterCode' && (
        <EnterCode
          onRedeem={onRedeem}
          onBack={redeemedCards.length || redeemedCardsV1.length ? moveToSummary : undefined}
          enableScan={hasCamera && config.GIFT_CARD_SCAN_ENABLED}
        />
      )}
      {currentStep === 'success' && (
        <Success
          redeemedCards={redeemedCards.length ? redeemedCards : redeemedCardsV1}
          onRedeemAnother={() => setCurrentStep('enterCode')}
          checkoutCurrency={checkoutCurrency}
        />
      )}
    </ModalBase>
  )
}

export default RedeemGiftCard
